import { Box, IconButton } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { PerUnitProduct } from '@vy/services';
import { INTEGRATION_PROVIDERS, type FixedProduct, type UsageProduct } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { FC, useState } from 'react';
import { VyTableCell } from '../../../../components/new-design/table/vy-table/VyTableCell';
import { useQuickbooksEnabled } from '../../../../hooks/requests/integrations';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';

type ErpCellProps = {
  product?: FixedProduct | PerUnitProduct | UsageProduct;
  productName?: string;
  value?: string;
  onEditErp?: (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => void;
  disableEdit?: boolean;
};

export const ErpCell: FC<ErpCellProps> = ({ value, product, productName, onEditErp, disableEdit = false }) => {
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite, {
    suspense: false,
  });
  const isQuickBooksEnabled = useQuickbooksEnabled();
  const isXeroEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.xero, {
    suspense: false,
  });

  const isErpConnected = isNetSuiteEnabled || isQuickBooksEnabled || isXeroEnabled;

  const [isHovering, setIsHovering] = useState(false);
  const canEdit = isNotNullOrUndefined(onEditErp) && isErpConnected && !disableEdit;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        width: '100%',
        height: '100%',
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <VyTableCell value={value} />
      {canEdit && (
        <IconButton
          size="small"
          sx={{
            opacity: isHovering ? 1 : 0,
            transition: 'opacity 0.2s',
            padding: '4px',
          }}
          onClick={(event) => {
            event.stopPropagation();

            if (isNullOrUndefined(product) || isNullOrUndefined(productName)) {
              return;
            }

            onEditErp?.(product, productName);
          }}
        >
          <VyIcon icon="Pen" transform="scale(0.8)" />
        </IconButton>
      )}
    </Box>
  );
};
